import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { RouteComponentProps, withRouter } from 'react-router';
import styled from 'styled-components';

import { WorkArea } from '@xsolla/ui-core';

import {
  ActiveUsersGraphic,
  AnalyticsSelectors,
  AuthMethodsGraphic,
  UsersCountriesGraphic
} from '../../components';
import { PageError, PageHeader, Preloader } from '../../components/ui';
import { useConfig } from '../../contexts/config';
import { useProjects } from '../../contexts/settings';
import { useAnalyticsData } from '../../hooks';
import { AnalyticsFilter, Period } from '../../hooks/types';
import { tokenExpiryErrorCheck } from '../../utils';
import DateTimeFormatOptions = Intl.DateTimeFormatOptions;

const PreloaderWrapper = styled.div`
  min-height: 100px;
`;

export const periods = {
  last7d: {
    from: moment().subtract(6, 'day').toDate(),
    to: moment().toDate()
  },
  last30d: {
    from: moment().subtract(29, 'day').toDate(),
    to: moment().toDate()
  },
  last60days: {
    from: moment().subtract(59, 'day').toDate(),
    to: moment().toDate()
  }
};

const dateFormat: DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  year: 'numeric'
};

const getAuthGraphTitle = (dates: Period, locale: string) =>
  `${new Date(dates.from).toLocaleDateString(locale, dateFormat)} – ${new Date(
    dates.to
  ).toLocaleDateString(locale, dateFormat)}`;

const Analytics = ({ history }: RouteComponentProps): JSX.Element => {
  const intl = useIntl();
  const { merchantId } = useConfig();
  const { projects, projectsFetching, error } = useProjects();
  const { xsollaAnalytics } = useConfig();

  const [selectedLogin, setSelectedLogin] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Period>(periods.last7d);
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const {
    users,
    errorUsersData,
    updateAnalytics,
    authMethods,
    errorAuthMethodsData,
    authCountries,
    errorAuthCountriesData
  } = useAnalyticsData({
    datePeriod: selectedDate,
    country: selectedCountry,
    loginProject: selectedLogin
  });

  useEffect(() => {
    xsollaAnalytics.sendOpen('login_pa_module__analytics_open', String(merchantId));
  }, []);

  const reloadPage = useCallback(() => {
    history.go(0);
  }, []);

  const loginPage = useCallback(() => {
    history.push('/');
  }, []);

  const updateAnalyticsHandler = useCallback(
    ({ datePeriod, loginProject, country }: AnalyticsFilter) => {
      setSelectedCountry((prev) => {
        return prev === country ? prev : country;
      });
      setSelectedLogin((prev) => {
        return prev === loginProject ? prev : loginProject;
      });
      setSelectedDate((prev) => {
        return prev === datePeriod ? prev : datePeriod;
      });
      updateAnalytics({ datePeriod, loginProject, country });
    },
    [selectedLogin, selectedDate, selectedCountry]
  );

  if (projectsFetching || (!projects && !error)) {
    return (
      <WorkArea>
        <PageHeader title={intl.formatMessage({ id: 'statistics.label' })} />
        <PreloaderWrapper>
          <Preloader />
        </PreloaderWrapper>
      </WorkArea>
    );
  }

  if (error) {
    const expiredToken = tokenExpiryErrorCheck(error.code);
    return (
      <PageError
        title={intl.formatMessage({ id: 'app.error.page.label' })}
        error={error}
        buttonText={intl.formatMessage({
          id: `${expiredToken ? 'button.login' : 'button.reload'}`
        })}
        pageName="dashboard"
        onClick={expiredToken ? loginPage : reloadPage}
      />
    );
  }

  const rangeTitle = getAuthGraphTitle(selectedDate, intl.locale);

  return (
    <WorkArea>
      <PageHeader title={intl.formatMessage({ id: 'statistics.label' })} />
      <AnalyticsSelectors
        loginProjects={projects || []}
        updateAnalytics={updateAnalyticsHandler}
        selectedLogin={selectedLogin}
        selectedDate={selectedDate}
        selectedCountry={selectedCountry}
      />
      <ActiveUsersGraphic users={users} error={errorUsersData} />
      <UsersCountriesGraphic
        countries={authCountries}
        error={errorAuthCountriesData}
        rangeTitle={rangeTitle}
      />
      <AuthMethodsGraphic
        authMethods={authMethods}
        error={errorAuthMethodsData}
        rangeTitle={rangeTitle}
      />
    </WorkArea>
  );
};

export default withRouter(Analytics);
